import dayjs from "dayjs";

import { parseUploadedUrl } from "@/Services/UploadPhoto";

import {
    StoredPhoto,
    StoredPhotoMeta,
    StoredPhotoSlot,
} from "../PhotoStorage.types";

interface StoredPhotoMetaFs extends StoredPhotoMeta {
    fileName: string;
}

const PHOTOS_DIR = "photos";
const SLOTS_DIR = "slots";
const META_EXT = ".json";

const MIME_TYPE_TO_EXTENSION: Record<string, string> = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/heic": "heic",
    "image/heif": "heif",
};

const encodeKey = (key: string): string =>
    encodeURIComponent(key).replace(/\./g, "%2E");

const getFileExtension = (file: File): string => {
    const nameParts = file.name.split(".");
    const extFromName =
        nameParts.length > 1 ? nameParts[nameParts.length - 1]?.trim() : "";

    if (extFromName) return extFromName.toLowerCase();

    return MIME_TYPE_TO_EXTENSION[file.type] ?? "bin";
};

class PhotoStorageFs {
    private root: FileSystemDirectoryHandle | null = null;
    private photosDir: FileSystemDirectoryHandle | null = null;
    private slotsDir: FileSystemDirectoryHandle | null = null;

    private async getRoot(): Promise<FileSystemDirectoryHandle> {
        if (!this.root) {
            this.root = await navigator.storage.getDirectory();
        }

        return this.root;
    }

    private encodePhotoIdForFs(id: string): string {
        return encodeURIComponent(id).replace(/\./g, "%2E");
    }

    private getPhotoMetaFileName(id: string): string {
        return `${this.encodePhotoIdForFs(id)}${META_EXT}`;
    }

    private getPhotoBinaryFileName(id: string, ext: string): string {
        return `${this.encodePhotoIdForFs(id)}.${ext}`;
    }

    private async getPhotosDir(): Promise<FileSystemDirectoryHandle> {
        if (!this.photosDir) {
            const root = await this.getRoot();
            this.photosDir = await root.getDirectoryHandle(PHOTOS_DIR, {
                create: true,
            });
        }

        return this.photosDir;
    }

    private async getSlotsDir(): Promise<FileSystemDirectoryHandle> {
        if (!this.slotsDir) {
            const root = await this.getRoot();
            this.slotsDir = await root.getDirectoryHandle(SLOTS_DIR, {
                create: true,
            });
        }

        return this.slotsDir;
    }

    private async writeJson<T>(
        dir: FileSystemDirectoryHandle,
        filename: string,
        data: T,
    ): Promise<void> {
        const fileHandle = await dir.getFileHandle(filename, { create: true });
        const writable = await fileHandle.createWritable();

        try {
            await writable.write(JSON.stringify(data));
        } finally {
            await writable.close();
        }
    }

    private async readJson<T>(
        dir: FileSystemDirectoryHandle,
        filename: string,
    ): Promise<T | null> {
        try {
            const fileHandle = await dir.getFileHandle(filename);
            const file = await fileHandle.getFile();
            const text = await file.text();

            return JSON.parse(text) as T;
        } catch {
            return null;
        }
    }

    private async deleteFile(
        dir: FileSystemDirectoryHandle,
        filename: string,
    ): Promise<void> {
        try {
            await dir.removeEntry(filename);
        } catch {
            // ignore missing file
        }
    }

    private async writeBinaryFile(
        dir: FileSystemDirectoryHandle,
        filename: string,
        file: Blob,
    ): Promise<void> {
        const fileHandle = await dir.getFileHandle(filename, { create: true });
        const writable = await fileHandle.createWritable();

        try {
            await writable.write(file);
        } finally {
            await writable.close();
        }
    }

    private async readBinaryFile(
        dir: FileSystemDirectoryHandle,
        filename: string,
    ): Promise<File | null> {
        try {
            const fileHandle = await dir.getFileHandle(filename);
            return await fileHandle.getFile();
        } catch {
            return null;
        }
    }

    private async blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);

            reader.readAsDataURL(blob);
        });
    }

    private async listJsonEntries<T>(
        dir: FileSystemDirectoryHandle,
    ): Promise<T[]> {
        const results: T[] = [];
        const entries = dir.entries();

        for await (const [name, handle] of entries) {
            if (handle.kind !== "file") continue;
            if (!name.endsWith(META_EXT)) continue;

            try {
                const file = await (handle as FileSystemFileHandle).getFile();
                const text = await file.text();
                results.push(JSON.parse(text) as T);
            } catch {
                // corrupted metadata file
            }
        }

        return results;
    }

    private async getPhotoMeta(id: string): Promise<StoredPhotoMetaFs | null> {
        const dir = await this.getPhotosDir();
        return this.readJson<StoredPhotoMetaFs>(
            dir,
            this.getPhotoMetaFileName(id),
        );
    }

    async savePhoto({
        id,
        file,
        fromSlot,
        skipUpload,
    }: {
        id: string;
        file: File;
        date: string;
        fromSlot: boolean;
        skipUpload?: boolean;
    }): Promise<void> {
        const dir = await this.getPhotosDir();
        const ext = getFileExtension(file);
        const binaryFileName = this.getPhotoBinaryFileName(id, ext);

        await this.writeBinaryFile(dir, binaryFileName, file);

        const photo: StoredPhotoMetaFs = {
            id,
            fileName: binaryFileName,
            name: file.name,
            type: file.type,
            size: file.size,
            taskComplete: false,
            uploadAttempts: 0,
            uploaded: false,
            fromSlot,
            skipUpload,
            createdAt: dayjs().toISOString(),
        };

        await this.writeJson(dir, this.getPhotoMetaFileName(id), photo);
    }

    async getPhoto(id: string): Promise<string | null> {
        const dir = await this.getPhotosDir();
        const photo = await this.getPhotoMeta(id);

        if (!photo) return null;

        const file = await this.readBinaryFile(dir, photo.fileName);
        if (!file) return null;

        return this.blobToBase64(file);
    }

    async getPhotoFile(id: string): Promise<File | null> {
        const dir = await this.getPhotosDir();
        const photo = await this.getPhotoMeta(id);

        if (!photo) return null;

        return this.readBinaryFile(dir, photo.fileName);
    }

    async getPhotoBlob(id: string): Promise<Blob | null> {
        const file = await this.getPhotoFile(id);
        return file ?? null;
    }

    async getAllPhotos(): Promise<StoredPhoto[]> {
        const dir = await this.getPhotosDir();
        const metas = await this.listJsonEntries<StoredPhotoMetaFs>(dir);

        const photos = await Promise.all(
            metas.map(async (meta) => {
                const file = await this.readBinaryFile(dir, meta.fileName);
                const base64 = file ? await this.blobToBase64(file) : "";

                const { fileName: _, ...rest } = meta;
                return { ...rest, base64 };
            }),
        );

        return photos.sort(
            (a, b) =>
                dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
        );
    }

    async getPhotosByIds(ids: string[]): Promise<StoredPhoto[]> {
        const dir = await this.getPhotosDir();

        const photos = await Promise.all(
            ids.map(async (id) => {
                const meta = await this.getPhotoMeta(id);
                if (!meta) return null;

                const file = await this.readBinaryFile(dir, meta.fileName);
                const base64 = file ? await this.blobToBase64(file) : "";

                const { fileName: _, ...rest } = meta;
                return { ...rest, base64 };
            }),
        );

        return photos
            .filter((p): p is StoredPhoto => p !== null)
            .sort(
                (a, b) =>
                    dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
            );
    }

    async deletePhoto(id: string): Promise<void> {
        const dir = await this.getPhotosDir();
        const photo = await this.getPhotoMeta(id);

        await this.deleteFile(dir, this.getPhotoMetaFileName(id));

        if (photo?.fileName) {
            await this.deleteFile(dir, photo.fileName);
        }
    }

    async deleteAllPhotos(): Promise<void> {
        const root = await this.getRoot();

        try {
            await root.removeEntry(PHOTOS_DIR, { recursive: true });
        } catch {
            // ignore if directory does not exist
        }

        this.photosDir = await root.getDirectoryHandle(PHOTOS_DIR, {
            create: true,
        });
    }

    async deletePhotosByIds(ids: string[]): Promise<void> {
        await Promise.all(ids.map((id) => this.deletePhoto(id)));
    }

    async getPhotosCount(): Promise<number> {
        const photos = await this.getAllPhotos();
        return photos.length;
    }

    async photoExists(id: string): Promise<boolean> {
        const dir = await this.getPhotosDir();

        try {
            await dir.getFileHandle(this.getPhotoMetaFileName(id));
            return true;
        } catch {
            return false;
        }
    }

    async getPhotoInfo(id: string): Promise<StoredPhotoMeta | null> {
        return this.getPhotoMeta(id);
    }

    async updatePhotoInfo(
        id: string,
        patch: Partial<Omit<StoredPhotoMeta, "id">>,
    ): Promise<void> {
        const dir = await this.getPhotosDir();
        const existing = await this.getPhotoMeta(id);

        if (!existing) {
            throw new Error(`Photo with id ${id} not found`);
        }

        const updated: StoredPhotoMetaFs = {
            ...existing,
            ...patch,
            id: existing.id,
            fileName: existing.fileName,
        };

        await this.writeJson(dir, this.getPhotoMetaFileName(id), updated);
    }

    async getTotalSize(): Promise<number> {
        const photos = await this.getAllPhotos();
        return photos.reduce((total, photo) => total + photo.size, 0);
    }

    async getSlotAndDelete(marketId: string): Promise<StoredPhotoSlot | null> {
        const dir = await this.getSlotsDir();
        const slots = await this.listJsonEntries<StoredPhotoSlot>(dir);
        const slot = slots.find((item) => item.marketId === marketId) ?? null;

        if (slot) {
            await this.deleteFile(dir, `${encodeKey(slot.url)}${META_EXT}`);
        }

        return slot;
    }

    async createSlot({ url }: { url: string }): Promise<void> {
        const parseData = parseUploadedUrl(url);
        if (!parseData) return;

        const { marketId, middlePath, filename } = parseData;
        const dir = await this.getSlotsDir();

        await this.writeJson(dir, `${encodeKey(url)}${META_EXT}`, {
            url,
            marketId,
            filename,
            middlePath,
        });
    }

    async deleteSlot(url: string): Promise<void> {
        const dir = await this.getSlotsDir();
        await this.deleteFile(dir, `${encodeKey(url)}${META_EXT}`);
    }

    // ------------- Kind
    async getKind(): Promise<"opfs"> {
        return "opfs";
    }
}

export const photoStorageFs = new PhotoStorageFs();

export { PhotoStorageFs };
